import React, { useCallback, useContext, useMemo, useState } from "react";
import {
  Event,
  OrganizerType,
  TicketV2,
  NotificationType,
  EventType,
} from "@markit/common.types";
import {
  isEventExternalLink,
  isEventRelatedType,
  isEventTicketsPaid,
  isExternalGenericLink,
  isProductNonPostType,
  isProductRelatedType,
  isProductSpecificType,
  isProductWithCheckout,
  isSubscriptionPaymentFailed,
  getCanonicalMediaUrls,
} from "@markit/common.utils";
import { useDispatch, useSelector } from "react-redux";
import { Colors } from "../../utils/colors";
import { updateEventManualSoldOut } from "../../utils/dashboardUtils";
import { deleteEventData, eventActions } from "../../redux/slices/eventSlice";
import { MixpanelContext } from "../../context/AnalyticsService";
import { getAccountState } from "../../redux/slices/accountSlice";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import HelpModal from "../../components/Containers/HelpModal";
import SettingsOption from "./SettingsOption";
import { exportEventData } from "../../utils/eventUtils/formUtils";
import AlertContainer from "../Containers/AlertContainer";
import ConfirmDeleteModal from "../Containers/ConfirmPopups/ConfirmDeleteModal";
import { Icon } from "@iconify/react";
import { useNavigate } from "../../hooks/useNavigate";
import CloneProductModal from "./DashboardModals/CloneProductModal";
import { showNotificationBanner } from "../../utils/notificationUtils";
import PaymentFailedButtonModal from "../DisplayModal/UpdatePaymentInfoModal";
import { NavigationId } from "../../navigation/AppParamList";
import ConfirmActionModal from "../Containers/ConfirmPopups/ConfirmActionModal";
import { EventRefundModal } from "./DashboardModals/EventRefundModal";
import { downloadMediaFromFirestore } from "../../utils/mediaUtils";

const getDashboardItemTypeLabel = (
  eventType: EventType,
  casing: "capitalize" | "lowercase" = "capitalize"
): string => {
  let label: string;
  if (isExternalGenericLink(eventType)) {
    label = "Form";
  } else if (eventType === EventType.POST) {
    label = "Post";
  } else if (isProductSpecificType(eventType)) {
    label = "Product";
  } else {
    label = "Event";
  }
  return casing === "lowercase" ? label.toLowerCase() : label;
};

type SettingsPanelProps = {
  event: Event;
  userTicket: TicketV2 | undefined;
  totalGuests: number;
  hasPaidRevenue: boolean;
  dashboardVisibility: number;
  isHost: boolean;
};

export const SettingsPanel = (props: SettingsPanelProps) => {
  const {
    event,
    userTicket,
    totalGuests,
    hasPaidRevenue,
    dashboardVisibility,
    isHost,
  } = props;
  const { account } = useSelector(getAccountState);
  const { accountData } = account;
  const mixpanel = useContext(MixpanelContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loadingDelete, setLoadingDelete] = useState(false);
  const [cloneModalVisible, setCloneModalVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [paymentFailedVisible, setPaymentFailedVisible] = useState(false);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);

  const styles = {
    container: { width: "100%" },

    contentHeader: {
      fontSize: 16,
      fontWeight: 600,
      marginBottom: 14,
    },

    optionsContainer: {
      backgroundColor: Colors.WHITE,
      width: "100%",
    },
  };

  const isExternalLink = useMemo(
    () => isEventExternalLink(event.eventType),
    [event.eventType]
  );

  const isProductSpecific = useMemo(
    () => isProductSpecificType(event.eventType),
    [event.eventType]
  );

  const itemTypeLabel = useMemo(
    () => getDashboardItemTypeLabel(event.eventType),
    [event.eventType]
  );

  const paymentFailed = useMemo(
    () => isSubscriptionPaymentFailed(accountData.customer.state),
    [accountData.customer.state]
  );

  // For both ticket sales for events and archiving for posts
  const changeSoldOutStatus = useCallback(
    async (label: string) => {
      await updateEventManualSoldOut(event);
      dispatch(
        eventActions.updateEvent({
          ...event,
          isManualSoldOut: !event.isManualSoldOut,
        })
      );
      showNotificationBanner(
        dispatch,
        label,
        event.isManualSoldOut
          ? NotificationType.AFFIRMATIVE
          : NotificationType.NEGATIVE,
        event.isManualSoldOut ? "ion:checkmark-circle" : "ion:close-circle"
      );
    },
    [dispatch, event]
  );

  const cloneOnPress = useCallback(async () => {
    setCloneModalVisible(true);
  }, []);

  const showConfirmDialog = useCallback(async () => {
    // todo (jonathan) don't allow deletion of an event over 100 attendees for risk of batch size
    if (userTicket && userTicket.role.type === OrganizerType.HOST) {
      if (
        isEventTicketsPaid(event) &&
        totalGuests !== null &&
        totalGuests > 0 &&
        !accountData.isAdmin
      ) {
        return setAlertText({
          heading: "Cannot delete paid or custom ticketed event.",
          subHeading: "This feature is not supported at this time.",
        });
      } else if (totalGuests !== null && totalGuests > 100) {
        return setAlertText({
          heading: "Cannot delete an event with over 100 attendees.",
          subHeading: "This feature is not supported at this time.",
        });
      }
      setDeleteConfirm(true);
    }
  }, [event, userTicket, totalGuests, accountData.isAdmin]);

  const confirmDeleteItem = useCallback(async () => {
    setLoadingDelete(true);
    await dispatch(deleteEventData(event, accountData.uid, mixpanel));
    if (accountData.inCreatorMode) {
      navigate(NavigationId.HOME_PRODUCTS);
    } else {
      navigate(`/u/${accountData.username}`, {
        state: { username: accountData.username },
      });
    }
    showNotificationBanner(
      dispatch,
      `${itemTypeLabel} Deleted`,
      NotificationType.NEGATIVE,
      "ion:trash"
    );
    setLoadingDelete(false);
  }, [
    dispatch,
    event,
    accountData.uid,
    accountData.inCreatorMode,
    accountData.username,
    mixpanel,
    itemTypeLabel,
    navigate,
  ]);

  const onExportFinish = useCallback(
    (success: boolean) => {
      if (success) {
        if (alertText.heading !== "Export Failed") {
          showNotificationBanner(
            dispatch,
            "Data Exported",
            NotificationType.AFFIRMATIVE
          );
        }
      } else {
        setAlertText({
          heading: "Export Failed",
          subHeading: "Please try again or contact our hotline.",
        });
      }
    },
    [alertText.heading, dispatch]
  );

  const exportData = useCallback(async () => {
    if (paymentFailed) {
      setPaymentFailedVisible(true);
      return;
    }
    await exportEventData(event, isExporting, setIsExporting, onExportFinish);
  }, [paymentFailed, event, isExporting, onExportFinish]);

  const downloadContent = useCallback(async () => {
    if (paymentFailed) {
      setPaymentFailedVisible(true);
      return;
    }
    const mediaUrls = getCanonicalMediaUrls(event);
    if (mediaUrls.length > 0) {
      setIsDownloading(true);
      await downloadMediaFromFirestore(mediaUrls, event.title);
      setIsDownloading(false);
    }
  }, [paymentFailed, event]);

  return (
    <>
      <HelpModal showModal={showHelpModal} setShowModal={setShowHelpModal} />
      <div
        className={isExternalLink ? "RowNormal" : "RowNormalSpaced"}
        style={{ gap: 30 }}
      >
        {isProductWithCheckout(event.eventType, event.accessLevel) ? (
          <div style={styles.container}>
            <div style={styles.contentHeader}>{itemTypeLabel} Options</div>
            <StandardBorderedContainer
              containerStyles={styles.optionsContainer}
            >
              <div>
                {/** Only available for events for now since isManualSoldOut is used for this and for archiving products. Need to separate fields */}
                <SettingsOption
                  title={
                    (event.isManualSoldOut ? "Resume " : "Pause ") +
                    (isProductSpecific
                      ? "Orders"
                      : isEventTicketsPaid(event)
                        ? "Ticket Sales"
                        : "RSVPs")
                  }
                  subtext={
                    isEventRelatedType(event.eventType) ? "" : "Coming soon..."
                  }
                  iconName={
                    event.isManualSoldOut
                      ? "ion:play-circle"
                      : "ion:pause-circle"
                  }
                  onPress={() => setConfirmPause(true)}
                  disabled={
                    dashboardVisibility < 2 ||
                    !isEventRelatedType(event.eventType)
                  }
                />
                {isEventTicketsPaid(event) ? (
                  <>
                    {isEventRelatedType(event.eventType) ? (
                      <HorizontalDivider />
                    ) : null}
                    <SettingsOption
                      title={
                        isProductSpecific ? "Refund Orders" : "Refund Event"
                      }
                      subtext={`This will refund all ${
                        isProductSpecific
                          ? "purchased orders"
                          : "ticket purchases"
                      }`}
                      iconName={"ion:return-up-back"}
                      onPress={() => setRefundModalVisible(true)}
                      disabled={!(isHost && hasPaidRevenue)}
                    />
                  </>
                ) : null}
              </div>
            </StandardBorderedContainer>
          </div>
        ) : null}
        <div style={styles.container}>
          <div style={styles.contentHeader}>Data</div>
          <StandardBorderedContainer containerStyles={styles.optionsContainer}>
            <div>
              {isProductNonPostType(event.eventType) ? (
                <>
                  <SettingsOption
                    title={`Download ${event.eventType}`}
                    subtext={"Export files to your computer"}
                    iconName={"ion:download"}
                    onPress={downloadContent}
                    disabled={dashboardVisibility < 2}
                    loading={isDownloading}
                  />
                  <HorizontalDivider />
                </>
              ) : null}
              {totalGuests > 0 ? (
                <>
                  <SettingsOption
                    title={"Export Data"}
                    subtext={"Download CSV file of all data"}
                    iconName={"ion:download"}
                    onPress={exportData}
                    disabled={dashboardVisibility < 2}
                    loading={isExporting}
                  />
                  <HorizontalDivider />
                </>
              ) : null}
              <SettingsOption
                title={"Help"}
                subtext={""}
                iconName={"ion:help"}
                onPress={() => setShowHelpModal(true)}
                disabled={false}
              />
            </div>
          </StandardBorderedContainer>
        </div>
        <div style={styles.container}>
          <div style={styles.contentHeader}>More</div>
          <StandardBorderedContainer containerStyles={styles.optionsContainer}>
            <div>
              {isProductRelatedType(event.eventType) ||
              isExternalGenericLink(event.eventType) ? (
                <>
                  <SettingsOption
                    title={`Clone ${itemTypeLabel}`}
                    subtext={
                      isExternalGenericLink(event.eventType) ||
                      isProductSpecific
                        ? `Create a new ${getDashboardItemTypeLabel(
                            event.eventType,
                            "lowercase"
                          )} with the same content`
                        : "Existing organizer roles won't be copied"
                    }
                    iconName={"ion:copy"}
                    onPress={cloneOnPress}
                    disabled={dashboardVisibility < 2}
                  />
                  <HorizontalDivider />
                </>
              ) : null}
              {isProductSpecific ? (
                <>
                  <SettingsOption
                    title={`${
                      event.isManualSoldOut ? "Unarchive" : "Archive"
                    } ${itemTypeLabel}`}
                    subtext={`Your ${getDashboardItemTypeLabel(
                      event.eventType,
                      "lowercase"
                    )} will be inactive if archived`}
                    iconName={"ion:archive"}
                    onPress={() => setConfirmArchive(true)}
                    disabled={dashboardVisibility < 2}
                  />
                  <HorizontalDivider />
                </>
              ) : null}
              <SettingsOption
                title={`Delete ${itemTypeLabel}`}
                subtext={
                  !isHost
                    ? "Only available for event owner"
                    : "This can't be undone"
                }
                iconName={"ion:trash"}
                onPress={showConfirmDialog}
                disabled={!isHost}
                altColor={!isHost ? undefined : Colors.RED1}
              />
            </div>
          </StandardBorderedContainer>
        </div>
        {isExternalLink ? <div style={styles.container} /> : null}
      </div>
      {cloneModalVisible ? (
        <CloneProductModal
          event={event}
          isModalVisible={cloneModalVisible}
          setIsModalVisible={setCloneModalVisible}
        />
      ) : null}
      {paymentFailedVisible ? (
        <PaymentFailedButtonModal
          isVisible={paymentFailedVisible}
          setIsVisible={setPaymentFailedVisible}
          location="Event Settings"
        />
      ) : null}
      {refundModalVisible ? (
        <EventRefundModal
          event={event}
          setModalVisible={setRefundModalVisible}
        />
      ) : null}
      <AlertContainer
        headerComp={
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <Icon
              icon={"ion:close-circle"}
              style={{ color: Colors.RED3 }}
              height={30}
            />
            {alertText.heading}
          </div>
        }
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
        altButtonLabel={
          alertText.heading === "Export Failed" ? "Got it" : undefined
        }
      />
      <ConfirmActionModal
        heading={`Are you sure you want to ${
          event.isManualSoldOut ? "unarchive" : "archive"
        } this ${getDashboardItemTypeLabel(event.eventType, "lowercase")}?`}
        subtext={`${
          event.isManualSoldOut
            ? `The ${getDashboardItemTypeLabel(
                event.eventType,
                "lowercase"
              )} will become accessible, but you may archive it at any time.`
            : `The ${getDashboardItemTypeLabel(
                event.eventType,
                "lowercase"
              )} will no longer be accessible, but you may unarchive it at any time.`
        }`}
        confirmButtonText="Continue"
        icon={<Icon icon="ion:archive" height={40} />}
        hideModal={!confirmArchive}
        setIsVisible={setConfirmArchive}
        confirmOnPress={() =>
          changeSoldOutStatus(
            event.isManualSoldOut
              ? `${itemTypeLabel} Unarchived`
              : `${itemTypeLabel} Archived`
          )
        }
        cancelOnPress={() => setConfirmArchive(false)}
      />
      <ConfirmActionModal
        heading={`Are you sure you want to ${
          event.isManualSoldOut ? "unpause" : "pause"
        } sales?`}
        subtext={`${
          event.isManualSoldOut
            ? `The ${getDashboardItemTypeLabel(
                event.eventType,
                "lowercase"
              )} page will allow sales, but you may pause it at any time.`
            : `The ${getDashboardItemTypeLabel(
                event.eventType,
                "lowercase"
              )} page will prevent any new sales, but you may unpause it at any time.`
        }`}
        confirmButtonText="Continue"
        icon={
          <Icon
            icon={
              event.isManualSoldOut ? "ion:play-circle" : "ion:pause-circle"
            }
            height={40}
          />
        }
        hideModal={!confirmPause}
        setIsVisible={setConfirmPause}
        confirmOnPress={() =>
          changeSoldOutStatus(
            event.isManualSoldOut ? "Sales Resumed" : "Sales Paused"
          )
        }
        cancelOnPress={() => setConfirmPause(false)}
      />
      <ConfirmDeleteModal
        heading="Are you sure you want to delete?"
        subtext="You cannot undo this."
        deleteButtonText="Delete"
        hideModal={!deleteConfirm}
        setIsVisible={setDeleteConfirm}
        buttonLoading={loadingDelete}
        deleteOnPress={confirmDeleteItem}
      />
    </>
  );
};
